import { createExternalStore } from "@client/utils/createExternalStore";
import {
  closeAppDrawer,
  openAppDrawer,
  useAppDrawer,
} from "@client/utils/exclusiveDrawer";
import { useEffect } from "react";

// Which recommender the unified "General Round Notes" drawer shows first
// (SP-2152 / SP-2153). `null` means the viewer's own note ("My general notes",
// editable for active recommenders). The triggers (the header notebook icon,
// the proposals-page card, the per-recommender affordance on a proposal's
// notes tab) render outside the round provider the drawer renders inside, so
// this module store bridges them. See `createExternalStore`.
//
// OPEN-STATE lives in `exclusiveDrawer.ts`, not here: this drawer is a peer of
// the Queue and To Do panels on the same screen edge, and at most one of them
// may be open. This store keeps only the payload — which member to show.
type GeneralNotesState = { memberId: string | null };

const store = createExternalStore<GeneralNotesState>({ memberId: null });

/** `useVisitedNavItems` key for the header notebook icon's "unseen" dot. */
export const GENERAL_NOTES_NAV_KEY = "general-notes";

/**
 * Open the drawer focused on a recommender (`memberId`), or on the viewer's own
 * note (`null`, the default) — which is editable when the viewer is an active
 * recommender.
 */
export const openGeneralNotes = (memberId: string | null = null) => {
  store.set({ memberId });
  openAppDrawer("generalNotes");
};

export const setGeneralNotesOpen = (open: boolean) => {
  if (open) openAppDrawer("generalNotes");
  else closeAppDrawer("generalNotes");
};

export const useGeneralNotes = (options?: { resetOnUnmount?: boolean }) => {
  const { memberId } = store.useValue();
  const [open] = useAppDrawer("generalNotes");
  const resetOnUnmount = options?.resetOnUnmount ?? false;

  // Same contract as before: the consumer unmounting (a navigation away from
  // the subtree the drawer renders in) clears the open flag, so a global
  // open-state can't leak across routes. Now that the flag is shared, only
  // THIS drawer's slot is released — a sibling that took the slot meanwhile is
  // left alone (`closeAppDrawer` is a no-op unless we still hold it).
  useEffect(() => {
    if (!resetOnUnmount) return;
    return () => {
      closeAppDrawer("generalNotes");
      store.set({ memberId: null });
    };
  }, [resetOnUnmount]);

  return { open, memberId };
};
